import { useEffect, useId, useRef, useState } from "react";
import clsx from "clsx";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Dialog, DialogCancelButton } from "@/components/ui/Dialog";
import {
  buildUploadEntries,
  uploadFilesToAuftraege,
} from "@/features/globalUpload/globalUploadApi";
import { setGlobalUploadSuppressed } from "@/features/globalUpload/globalUploadSuppress";
import { ApiError } from "@/lib/apiClient";
import { toast } from "@/lib/toast";
import {
  DOKUMENT_STATUS_LABELS,
  getDokument,
  updateDokumentStatus,
  type DokumentDetail,
} from "@/lib/dokumente";
import { AB_FROM_ANGEBOT_Z } from "@/pages/dokumente/documentEditorLayers";

function buildBeauftragungCustomName(
  file: File,
  auftragNummer: string | null | undefined,
): string {
  const dot = file.name.lastIndexOf(".");
  const ext = dot >= 0 ? file.name.slice(dot) : ".pdf";
  const proj = String(auftragNummer || "").trim();
  if (proj) return `${proj}-Kundenbestellung${ext}`;
  return file.name;
}

export interface AngebotAngenommenDialogProps {
  open: boolean;
  angebot: DokumentDetail | null;
  zIndexClass?: string;
  onClose: () => void;
  /** Nach erfolgreichem Angenommen (mit oder ohne Beauftragung). */
  onAccepted: (updated: DokumentDetail) => void;
}

export function AngebotAngenommenDialog({
  open,
  angebot,
  zIndexClass = AB_FROM_ANGEBOT_Z,
  onClose,
  onAccepted,
}: AngebotAngenommenDialogProps) {
  const inputId = useId();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const dropCounterRef = useRef(0);
  const [file, setFile] = useState<File | null>(null);
  const [dropActive, setDropActive] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canUpload =
    angebot != null &&
    angebot.auftrag_id != null &&
    Number.isFinite(angebot.auftrag_id) &&
    angebot.auftrag_id > 0;

  useEffect(() => {
    if (!open) {
      setGlobalUploadSuppressed(false);
      setFile(null);
      setDropActive(false);
      dropCounterRef.current = 0;
      setSubmitting(false);
      setError(null);
      return;
    }
    setGlobalUploadSuppressed(true);
    return () => setGlobalUploadSuppressed(false);
  }, [open]);

  function assignFile(next: File | null) {
    setFile(next);
    setError(null);
  }

  function onFilesPicked(list: FileList | null) {
    const first = list?.[0] ?? null;
    assignFile(first);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }

  async function continueWithoutBeauftragung() {
    if (!angebot) return;
    setSubmitting(true);
    setError(null);
    try {
      const updated = await updateDokumentStatus(angebot.id, "angenommen");
      toast.success(`Status: ${DOKUMENT_STATUS_LABELS.angenommen}`);
      onAccepted(updated);
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Statuswechsel fehlgeschlagen.");
    } finally {
      setSubmitting(false);
    }
  }

  async function uploadAndContinue() {
    if (!angebot || !file || !canUpload || angebot.auftrag_id == null) return;
    setSubmitting(true);
    setError(null);
    try {
      const auftragId = angebot.auftrag_id;
      const entry = {
        id: "beauftragung",
        original: file,
        customName: buildBeauftragungCustomName(file, angebot.auftrag_nummer),
        hasConflict: false,
      };
      const uploadEntries = buildUploadEntries([entry], [auftragId]);
      await uploadFilesToAuftraege(uploadEntries, {
        [auftragId]: [{ angebot_id: angebot.id, file_index: 0 }],
      });
      window.dispatchEvent(new Event("auftraege_changed"));
      window.dispatchEvent(new Event("dokumente_changed"));
      const updated = await getDokument(angebot.id);
      toast.success("Beauftragung abgelegt — Angebot angenommen.");
      onAccepted(updated);
      onClose();
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : "Beauftragung konnte nicht abgelegt werden.",
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog
      open={open}
      onClose={submitting ? () => undefined : onClose}
      title="Angebot annehmen"
      description="Wurde eine Bestellung oder Beauftragung zugesendet? Wenn ja, bitte jetzt ablegen — danach geht es mit Bestellnummer und Auftragsbestätigung weiter."
      size="lg"
      zIndexClass={zIndexClass}
      footer={
        <div className="flex flex-wrap justify-end gap-2">
          <DialogCancelButton onClose={onClose} disabled={submitting} />
          <Button
            type="button"
            variant="secondary"
            onClick={() => void continueWithoutBeauftragung()}
            disabled={submitting || !angebot}
            loading={submitting && !file}
          >
            Ohne Beauftragung fortfahren
          </Button>
          <Button
            type="button"
            onClick={() => void uploadAndContinue()}
            disabled={submitting || !angebot || !file || !canUpload}
            loading={submitting && Boolean(file)}
          >
            Ablegen und fortfahren
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {error ? <Alert variant="error">{error}</Alert> : null}

        {!canUpload ? (
          <Alert variant="warning">
            Dieses Angebot ist keinem Projekt zugeordnet. Eine Beauftragung kann hier nicht
            abgelegt werden — bitte ohne Beauftragung fortfahren oder zuerst ein Projekt
            verknüpfen.
          </Alert>
        ) : (
          <>
            <p className="text-sm text-label-secondary">
              Legen Sie die Bestellung bzw. Beauftragung im Projektordner ab. Anschließend
              tragen Sie die Bestellnummer ein und erstellen die Auftragsbestätigung.
            </p>

            <input
              id={inputId}
              ref={fileInputRef}
              type="file"
              accept=".pdf,application/pdf,image/*"
              className="hidden"
              onChange={(e) => onFilesPicked(e.target.files)}
            />

            <div
              role="button"
              tabIndex={0}
              aria-controls={inputId}
              className={clsx(
                "flex min-h-[8.5rem] cursor-pointer flex-col items-center justify-center rounded-ios border-2 border-dashed px-4 py-6 text-center transition",
                dropActive
                  ? "border-accent bg-accent/10 text-label"
                  : "border-separator text-label-secondary hover:border-accent hover:bg-accent/5",
                submitting && "pointer-events-none opacity-60",
              )}
              onClick={() => {
                if (!submitting) fileInputRef.current?.click();
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  if (!submitting) fileInputRef.current?.click();
                }
              }}
              onDragEnter={(e) => {
                if (!e.dataTransfer?.types?.includes("Files")) return;
                e.preventDefault();
                e.stopPropagation();
                dropCounterRef.current += 1;
                setDropActive(true);
              }}
              onDragOver={(e) => {
                if (!e.dataTransfer?.types?.includes("Files")) return;
                e.preventDefault();
                e.stopPropagation();
                e.dataTransfer.dropEffect = "copy";
              }}
              onDragLeave={(e) => {
                if (!e.dataTransfer?.types?.includes("Files")) return;
                e.preventDefault();
                e.stopPropagation();
                dropCounterRef.current = Math.max(0, dropCounterRef.current - 1);
                if (dropCounterRef.current === 0) setDropActive(false);
              }}
              onDrop={(e) => {
                if (!e.dataTransfer?.files?.length) return;
                e.preventDefault();
                e.stopPropagation();
                dropCounterRef.current = 0;
                setDropActive(false);
                onFilesPicked(e.dataTransfer.files);
              }}
            >
              {file ? (
                <>
                  <p className="text-sm font-medium text-label">{file.name}</p>
                  <p className="mt-1 text-xs text-label-tertiary">
                    {(file.size / 1024 / 1024).toFixed(1)} MB · Klicken zum Austauschen
                  </p>
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    className="mt-3"
                    disabled={submitting}
                    onClick={(e) => {
                      e.stopPropagation();
                      assignFile(null);
                    }}
                  >
                    Entfernen
                  </Button>
                </>
              ) : (
                <>
                  <p className="text-sm font-medium text-label">
                    Bestellung / Beauftragung hier ablegen
                  </p>
                  <p className="mt-1 text-xs text-label-tertiary">
                    PDF oder Bild · wird im SharePoint-Projektordner gespeichert
                  </p>
                </>
              )}
            </div>
          </>
        )}
      </div>
    </Dialog>
  );
}
